/**
 * StatusDrilldownModal — opens when an admin clicks a "Ticket Status
 * Distribution" tile on the Admin Analytics Dashboard / CAO War Room.
 * Shows enriched per-ticket rows with:
 *   • Department filter (dropdown, derived from loaded rows)
 *   • Custom date range (From / To, applied on `created_at`)
 *   • Latest ticket status update column
 *   • CSV export
 *   • Print
 * Filters are client-side on already-loaded rows for snappy interaction.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { analyticsAPI } from '@/lib/api';
import { Download, Printer, RefreshCw, X } from 'lucide-react';

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const csvEscape = (val) => {
  if (val === null || val === undefined) return '';
  const s = String(val).replace(/"/g, '""');
  return /[",\n]/.test(s) ? `"${s}"` : s;
};

const downloadCsv = (status, rows) => {
  const headers = [
    'Ticket ID', 'Created At', 'Status', 'Created By', 'Email', 'Role',
    'Problem Location', 'Description', 'Department', 'Resolver',
    'Resolver Email', 'Latest Update By', 'Latest Update At',
    'Latest Update Remark',
  ];
  const lines = [headers.join(',')];
  rows.forEach((r) => {
    lines.push([
      r.ticket_id, fmtDateTime(r.created_at), r.status, r.created_by_name,
      r.created_by_email, r.created_by_role, r.problem_location, r.description,
      r.department, r.resolver_name, r.resolver_email,
      r.latest_update?.by ?? '',
      fmtDateTime(r.latest_update?.at), r.latest_update?.remark ?? '',
    ].map(csvEscape).join(','));
  });
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  const safeStatus = status.toLowerCase().replace(/\s+/g, '-');
  a.download = `status-${safeStatus}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const printRows = (status, rows, filterSummary) => {
  // Build a self-contained print view in a hidden iframe so the parent
  // page doesn't get its layout printed. Using an iframe (not a new
  // window) avoids popup blockers on every browser we support.
  const esc = (s) =>
    s === null || s === undefined
      ? ''
      : String(s)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');

  const rowsHtml = rows.map((r) => `
    <tr>
      <td>
        <div><strong>${esc(r.ticket_id)}</strong></div>
        <div class="muted">${esc(fmtDateTime(r.created_at))}</div>
      </td>
      <td>${esc(r.status)}</td>
      <td>
        <div>${esc(r.created_by_name || '—')}</div>
        <div class="muted">${esc(r.created_by_email || '')}</div>
      </td>
      <td>${esc(r.problem_location || '—')}</td>
      <td>${esc(r.department || '')}</td>
      <td>
        <div>${esc(r.resolver_name || 'Unassigned')}</div>
        <div class="muted">${esc(r.resolver_email || '')}</div>
      </td>
      <td>
        <div>${esc(r.latest_update?.remark || '—')}</div>
        <div class="muted">
          ${r.latest_update?.by ? `by ${esc(r.latest_update.by)} · ` : ''}${esc(fmtDateTime(r.latest_update?.at))}
        </div>
      </td>
      <td>${esc((r.description || '').slice(0, 240))}</td>
    </tr>
  `).join('');

  const html = `<!doctype html>
  <html><head><meta charset="utf-8"><title>${esc(status)} tickets — ${new Date().toLocaleString()}</title>
  <style>
    body { font-family: Inter, -apple-system, Segoe UI, sans-serif; color:#0f172a; margin:24px; }
    h1 { font-size: 18px; margin: 0 0 6px 0; color:#1e3a5f; }
    .meta { font-size: 11px; color:#64748b; margin-bottom: 16px; }
    table { width: 100%; border-collapse: collapse; font-size: 11px; }
    th { background:#1e3a5f; color:#fff; text-align:left; padding:6px 8px; }
    td { border-bottom: 1px solid #e2e8f0; padding:6px 8px; vertical-align: top; }
    .muted { color:#64748b; font-size:10px; }
    tr { page-break-inside: avoid; }
    @media print { body { margin: 12mm; } }
  </style></head>
  <body>
    <h1>VGWS Care Desk · ${esc(status)} tickets (${rows.length})</h1>
    <div class="meta">Generated ${esc(new Date().toLocaleString())}${filterSummary ? ` · ${esc(filterSummary)}` : ''}</div>
    <table>
      <thead><tr>
        <th>Ticket</th><th>Status</th><th>Created By</th><th>Problem Location</th>
        <th>Department</th><th>Resolver</th>
        <th>Latest Update</th><th>Description</th>
      </tr></thead>
      <tbody>${rowsHtml || '<tr><td colspan="8" style="text-align:center;color:#64748b;">No tickets matched the current filters.</td></tr>'}</tbody>
    </table>
  </body></html>`;

  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
  // Wait one tick so the browser parses styles before printing.
  setTimeout(() => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } finally {
      // Remove the iframe after the user finishes / cancels print.
      setTimeout(() => document.body.removeChild(iframe), 1500);
    }
  }, 100);
};

const toDateOnly = (iso) => {
  if (!iso) return null;
  try {
    return new Date(iso).toISOString().slice(0, 10);
  } catch {
    return null;
  }
};

const StatusDrilldownModal = ({ status, open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);
  const [deptFilter, setDeptFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = useCallback(async () => {
    if (!status) return;
    // Clear stale rows the moment a new fetch starts so the count badge
    // + table never flash the previous status's numbers while the next
    // request is in flight. Prevents the "0 tickets" glitch users saw
    // when opening a new tile immediately after closing another.
    setRows([]);
    setLoading(true);
    setError(null);
    try {
      const res = await analyticsAPI.getStatusTickets(status);
      setRows(res.data?.tickets || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Unable to load tickets right now. Retrying on next open.');
      if (process.env.NODE_ENV !== 'production') {
        console.debug('StatusDrilldownModal load failed:', e?.message);
      }
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    if (open && status) load();
  }, [open, status, load]);

  // Reset filters whenever the modal re-opens for a new status.
  useEffect(() => {
    if (open) {
      setDeptFilter('all');
      setDateFrom('');
      setDateTo('');
    }
  }, [open, status]);

  // Unique department list derived from loaded rows (so we never show a
  // filter option that won't return any results).
  const departmentOptions = useMemo(() => {
    const set = new Set();
    rows.forEach((r) => {
      if (r.department) set.add(r.department);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (deptFilter !== 'all' && r.department !== deptFilter) return false;
      const day = toDateOnly(r.created_at);
      if (dateFrom && day && day < dateFrom) return false;
      if (dateTo && day && day > dateTo) return false;
      return true;
    });
  }, [rows, deptFilter, dateFrom, dateTo]);

  // Human-readable filter summary line for the CSV / print footer.
  const filterSummary = useMemo(() => {
    const parts = [];
    if (deptFilter !== 'all') parts.push(`Department: ${deptFilter}`);
    if (dateFrom || dateTo) parts.push(`Date: ${dateFrom || '…'} → ${dateTo || '…'}`);
    return parts.join(' · ');
  }, [deptFilter, dateFrom, dateTo]);

  const clearFilters = () => {
    setDeptFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  const anyFilterActive = deptFilter !== 'all' || dateFrom !== '' || dateTo !== '';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="status-drilldown-modal"
        className="max-w-6xl bg-slate-900 text-white border-slate-700 max-h-[90vh] overflow-hidden flex flex-col"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span>{status} tickets</span>
            {loading ? (
              <Badge className="bg-slate-600 text-white animate-pulse" data-testid="drilldown-count-badge">Loading…</Badge>
            ) : (
              <Badge className="bg-[#E97832] text-white" data-testid="drilldown-count-badge">
                {filteredRows.length}
                {filteredRows.length !== rows.length && (
                  <span className="ml-1 opacity-70">/ {rows.length}</span>
                )}
              </Badge>
            )}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {status === 'Pending'
              ? 'Tickets created over 12 hours ago that have not moved out of "Open".'
              : 'Click any row to inspect the full ticket detail page. Use the filters below to narrow by department or date.'}
          </DialogDescription>
        </DialogHeader>

        {/* Filter bar — department + date range. Filters apply instantly
            (client-side) so the user can pivot without refetching. */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2" data-testid="drilldown-filters">
          <div className="flex flex-col">
            <label className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Department</label>
            <select
              data-testid="drilldown-dept-filter"
              value={deptFilter}
              onChange={(e) => setDeptFilter(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded-md px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:border-[#E97832]"
            >
              <option value="all">All departments</option>
              {departmentOptions.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">From (created on or after)</label>
            <input
              type="date"
              data-testid="drilldown-date-from"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => setDateFrom(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded-md px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:border-[#E97832]"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">To (created on or before)</label>
            <input
              type="date"
              data-testid="drilldown-date-to"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => setDateTo(e.target.value)}
              className="bg-slate-800 border border-slate-700 rounded-md px-2 py-1.5 text-sm text-slate-100 focus:outline-none focus:border-[#E97832]"
            />
          </div>
          <div className="flex flex-col justify-end">
            {anyFilterActive ? (
              <Button
                data-testid="drilldown-clear-filters"
                size="sm"
                variant="outline"
                onClick={clearFilters}
                className="border-slate-600 text-slate-200 bg-transparent hover:bg-slate-800 h-[34px]"
              >
                <X className="w-3.5 h-3.5 mr-2" />
                Clear filters
              </Button>
            ) : (
              <div className="text-[11px] text-slate-500 self-end mb-1">
                {filteredRows.length === rows.length ? 'No filters applied' : ''}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 mb-2">
          <Button
            data-testid="drilldown-refresh-btn"
            size="sm"
            variant="outline"
            onClick={load}
            disabled={loading}
            className="border-slate-600 text-slate-200 bg-transparent hover:bg-slate-800"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button
            data-testid="drilldown-csv-btn"
            size="sm"
            onClick={() => downloadCsv(status, filteredRows)}
            disabled={loading || filteredRows.length === 0}
            className="bg-[#E97832] hover:bg-[#d56b2c]"
          >
            <Download className="w-3.5 h-3.5 mr-2" />
            Download CSV
          </Button>
          <Button
            data-testid="drilldown-print-btn"
            size="sm"
            variant="outline"
            onClick={() => printRows(status, filteredRows, filterSummary)}
            disabled={loading || filteredRows.length === 0}
            className="border-slate-600 text-slate-200 bg-transparent hover:bg-slate-800"
          >
            <Printer className="w-3.5 h-3.5 mr-2" />
            Print
          </Button>
          {anyFilterActive && (
            <span className="text-[11px] text-slate-400 ml-2" data-testid="drilldown-filter-summary">
              {filterSummary}
            </span>
          )}
        </div>

        {error && (
          <div className="text-amber-300 text-sm border border-amber-500/40 bg-amber-500/10 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <div className="overflow-auto flex-1 rounded-md border border-slate-700">
          <table className="w-full text-sm">
            <thead className="bg-slate-800 sticky top-0 z-10">
              <tr className="text-left text-slate-300">
                <th className="px-3 py-2 font-medium">Ticket</th>
                <th className="px-3 py-2 font-medium">Created By</th>
                <th className="px-3 py-2 font-medium">Problem Location</th>
                <th className="px-3 py-2 font-medium">Description</th>
                <th className="px-3 py-2 font-medium">Department</th>
                <th className="px-3 py-2 font-medium">Resolver</th>
                <th className="px-3 py-2 font-medium">Latest Update</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400" data-testid="drilldown-loading-row">
                  <span className="inline-flex items-center gap-2">
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    Loading tickets…
                  </span>
                </td></tr>
              )}
              {!loading && filteredRows.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">
                  {rows.length === 0
                    ? 'No tickets in this bucket.'
                    : 'No tickets match the current filters.'}
                </td></tr>
              )}
              {!loading && filteredRows.map((r) => (
                <tr
                  key={r.ticket_id}
                  data-testid={`drilldown-row-${r.ticket_id}`}
                  className="border-t border-slate-800 hover:bg-slate-800/50"
                >
                  <td className="px-3 py-2 font-mono text-xs text-slate-200">
                    <div>{r.ticket_id}</div>
                    <div className="text-slate-500">{fmtDateTime(r.created_at)}</div>
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-slate-100">{r.created_by_name || '—'}</div>
                    <div className="text-xs text-slate-500">{r.created_by_email}</div>
                  </td>
                  <td className="px-3 py-2 text-slate-200">{r.problem_location || '—'}</td>
                  <td className="px-3 py-2 text-slate-300 max-w-[16rem]">
                    <span className="line-clamp-2">{r.description}</span>
                  </td>
                  <td className="px-3 py-2 text-slate-200" data-testid={`drilldown-department-${r.ticket_id}`}>{r.department}</td>
                  <td className="px-3 py-2">
                    <div className="text-slate-100">{r.resolver_name}</div>
                    <div className="text-xs text-slate-500">{r.resolver_email}</div>
                  </td>
                  <td className="px-3 py-2 max-w-[18rem]">
                    <div className="text-slate-100 line-clamp-2">{r.latest_update?.remark}</div>
                    <div className="text-xs text-slate-500">
                      {r.latest_update?.by ? `by ${r.latest_update.by} · ` : ''}{fmtDateTime(r.latest_update?.at)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default StatusDrilldownModal;
